import { Container, Graphics } from "pixi.js";
import type { ChanceMarkerTuning } from "./chanceTuning";

/**
 * The detonation's target markers: a rounded, semi-transparent square behind each symbol the barrel is about to
 * blast. Mounted under the symbols in the cells' own space (`GridContainer.cellUnderlay`, beside `gridRoot`), so a
 * cell's local position is its marker's centre. The presenter fades each marker in on its own (`setMarkerOpacity`: the
 * stagger) and the whole set out together (`setOpacity`).
 */
export class ChanceMarkersView extends Container {
  private readonly tuning: ChanceMarkerTuning;

  public constructor(tuning: ChanceMarkerTuning) {
    super({ label: "ChanceMarkers" });
    this.tuning = tuning;
    this.visible = false;
  }

  /** One marker centred on each cell, in the order given, cut to the cell's size; replaces any shown. Each starts transparent. */
  public show(cellCentres: readonly { x: number; y: number }[], cellSize: { width: number; height: number }): void {
    this.clear();
    const t = this.tuning;
    const side = Math.min(cellSize.width, cellSize.height) * t.sizeRatio;
    for (const centre of cellCentres) {
      const marker = new Graphics().roundRect(-side / 2, -side / 2, side, side, side * t.cornerRatio).fill({ color: t.color, alpha: t.alpha });
      marker.label = "ChanceMarker";
      marker.position.set(centre.x, centre.y);
      marker.alpha = 0;
      marker.visible = false;
      this.addChild(marker);
    }
    this.setOpacity(1);
  }

  /** One marker's own opacity, 0..1 (hidden at 0), by its place in `show`'s order. */
  public setMarkerOpacity(order: number, opacity: number): void {
    const marker = this.children[order];
    marker.alpha = opacity;
    marker.visible = opacity > 0;
  }

  /** The whole set's opacity, 0..1; hidden at 0. */
  public setOpacity(opacity: number): void {
    this.alpha = opacity;
    this.visible = opacity > 0 && this.children.length > 0;
  }

  /** Removes every marker. */
  public clear(): void {
    for (const marker of this.removeChildren()) marker.destroy();
    this.visible = false;
  }
}
